import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import {
  getBulkPurchaseOrders,
  previewBulkPurchaseRefund,
  processBulkPurchaseRefund,
} from '../../api/adminService';

const STATUS_TABS = [
  { key: 'paid', label: '결제 완료' },
  { key: 'pending', label: '진행 중' },
  { key: 'failed', label: '실패' },
  { key: 'all', label: '전체' },
];

const STATUS_LABEL = {
  paid: { text: '결제 완료', color: '#10B981', bg: '#ECFDF5' },
  pending: { text: '진행 중', color: '#F59E0B', bg: '#FFFBEB' },
  failed: { text: '실패', color: '#EF4444', bg: '#FEF2F2' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const BulkPurchaseAdminPage = () => {
  const navigate = useNavigate();
  const [user, loadingAuth] = useAuthState(auth);
  const [status, setStatus] = useState('paid');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({ paid: 0, pending: 0, failed: 0 });

  // 환불 처리 모달 상태
  const [refundModalOrder, setRefundModalOrder] = useState(null);  // 클릭한 order 객체
  const [refundPreview, setRefundPreview] = useState(null);        // preview API 응답
  const [refundPreviewLoading, setRefundPreviewLoading] = useState(false);
  const [refundPreviewError, setRefundPreviewError] = useState(null);
  const [refundReason, setRefundReason] = useState('고객 요청');
  const [refundSendNotifications, setRefundSendNotifications] = useState(true);
  const [refundForceOutsideWindow, setRefundForceOutsideWindow] = useState(false);
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundProcessError, setRefundProcessError] = useState(null);
  const [refundProcessResult, setRefundProcessResult] = useState(null);

  const fetchItems = useCallback(async (statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBulkPurchaseOrders(statusFilter, 200);
      setItems(res.items || []);
    } catch (err) {
      setError(err.message || '주문 목록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const [p, pen, f] = await Promise.all([
        getBulkPurchaseOrders('paid', 500),
        getBulkPurchaseOrders('pending', 500),
        getBulkPurchaseOrders('failed', 500),
      ]);
      setCounts({
        paid: p.count || 0,
        pending: pen.count || 0,
        failed: f.count || 0,
      });
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (loadingAuth) return;
    if (!user) {
      navigate('/auth?redirect=/admin/bulk-purchases');
      return;
    }
    fetchItems(status);
    fetchCounts();
  }, [user, loadingAuth, status, navigate, fetchItems, fetchCounts]);

  // 환불 처리 모달 열기 → preview 호출
  const openRefundModal = useCallback(async (order) => {
    setRefundModalOrder(order);
    setRefundPreview(null);
    setRefundPreviewError(null);
    setRefundProcessError(null);
    setRefundProcessResult(null);
    setRefundReason('고객 요청');
    setRefundSendNotifications(true);
    setRefundForceOutsideWindow(false);
    setRefundPreviewLoading(true);

    try {
      const res = await previewBulkPurchaseRefund(order.id || order.order_id);
      setRefundPreview(res?.preview || null);
    } catch (err) {
      setRefundPreviewError(err.message || '환불 미리보기 호출에 실패했습니다.');
    } finally {
      setRefundPreviewLoading(false);
    }
  }, []);

  const closeRefundModal = useCallback(() => {
    if (refundProcessing) return; // 처리 중에는 닫기 막기
    setRefundModalOrder(null);
    setRefundPreview(null);
    setRefundPreviewError(null);
    setRefundProcessError(null);
    setRefundProcessResult(null);
  }, [refundProcessing]);

  const submitRefundProcess = useCallback(async () => {
    if (!refundModalOrder) return;
    if (!refundReason.trim()) {
      setRefundProcessError('환불 사유는 필수입니다.');
      return;
    }
    if (refundPreview && !refundPreview.is_within_refund_window && !refundForceOutsideWindow) {
      setRefundProcessError('환불 가능 기간(7일)을 초과했습니다. 강제 진행 체크박스를 활성화해주세요.');
      return;
    }

    const orderId = refundModalOrder.id || refundModalOrder.order_id;
    setRefundProcessing(true);
    setRefundProcessError(null);

    try {
      const res = await processBulkPurchaseRefund(orderId, {
        refundReason: refundReason.trim(),
        sendNotifications: refundSendNotifications,
        forceOutsideWindow: refundForceOutsideWindow,
      });
      setRefundProcessResult(res);
      // 목록·카운트 갱신
      fetchItems(status);
      fetchCounts();
    } catch (err) {
      setRefundProcessError(err.message || '환불 처리 중 오류가 발생했습니다.');
    } finally {
      setRefundProcessing(false);
    }
  }, [
    refundModalOrder,
    refundReason,
    refundSendNotifications,
    refundForceOutsideWindow,
    refundPreview,
    fetchItems,
    fetchCounts,
    status,
  ]);

  const totalRevenue = items
    .filter((i) => i.status === 'paid')
    .reduce((sum, i) => sum + (i.total_amount || 0), 0);
  const totalCoupons = items
    .filter((i) => i.status === 'paid')
    .reduce((sum, i) => sum + (i.quantity || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', padding: '24px 16px' }}>
      <Helmet><title>대량 구매 관리 — 왓퀴즈AI</title></Helmet>

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>대량 구매 관리</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6B7280' }}>
            비회원 카드 결제로 발급된 쿠폰 N개 주문 모니터링
          </p>
        </div>

        {/* 요약 카드 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
          <SummaryCard label="결제 완료 건수" value={counts.paid} color="#10B981" />
          <SummaryCard label="현재 화면 매출" value={`₩${totalRevenue.toLocaleString()}`} color="#3182F6" />
          <SummaryCard label="현재 화면 쿠폰" value={`${totalCoupons}개`} color="#8B5CF6" />
          <SummaryCard label="진행 중" value={counts.pending} color="#F59E0B" />
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatus(tab.key)}
              style={{
                padding: '8px 16px',
                borderRadius: 10,
                border: '1px solid #E5E7EB',
                background: status === tab.key ? '#3182F6' : '#fff',
                color: status === tab.key ? '#fff' : '#374151',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 리스트 */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <p style={{ padding: 32, textAlign: 'center', color: '#6B7280' }}>불러오는 중…</p>
          ) : error ? (
            <p style={{ padding: 32, textAlign: 'center', color: '#EF4444' }}>{error}</p>
          ) : items.length === 0 ? (
            <p style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>주문이 없습니다.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#F9FAFB' }}>
                <tr>
                  <th style={th}>학교</th>
                  <th style={th}>플랜 / 수량</th>
                  <th style={th}>금액</th>
                  <th style={th}>결제자</th>
                  <th style={th}>상태</th>
                  <th style={th}>발송</th>
                  <th style={th}>환불</th>
                  <th style={th}>일시</th>
                </tr>
              </thead>
              <tbody>
                {items.map((order) => {
                  const s = STATUS_LABEL[order.status] || { text: order.status, color: '#6B7280', bg: '#F3F4F6' };
                  return (
                    <tr key={order.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                      <td style={td}>
                        <strong>{order.school_name}</strong>
                        <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'ui-monospace, monospace' }}>{order.coupon_prefix}</div>
                      </td>
                      <td style={td}>
                        {order.plan_short_label} × {order.quantity}개
                      </td>
                      <td style={{ ...td, fontWeight: 700, color: '#3182F6' }}>
                        ₩{(order.total_amount || 0).toLocaleString()}
                      </td>
                      <td style={td}>
                        <div>{order.contact_name}</div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>{order.email}</div>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>{order.phone}</div>
                      </td>
                      <td style={td}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
                          {s.text}
                        </span>
                      </td>
                      <td style={td}>
                        <div style={{ fontSize: 11, color: '#6B7280' }}>
                          📧 {order.notification?.email_sent ? '✓' : '✗'}
                          {' · '}
                          📱 {order.notification?.sms_sent ? '✓' : '✗'}
                        </div>
                        {order.receipt_url && (
                          <a href={order.receipt_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#3182F6' }}>매출전표</a>
                        )}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          {order.refund_status === 'requested' && (
                            <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#FEF2F2', color: '#991B1B' }}>요청됨</span>
                          )}
                          {order.refund_status === 'processing' && (
                            <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#FFFBEB', color: '#92400E' }}>처리 중</span>
                          )}
                          {order.refund_status === 'completed' && (
                            <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#F3F4F6', color: '#6B7280' }}>완료</span>
                          )}
                          {order.refund_status === 'failed' && (
                            <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#FEF2F2', color: '#991B1B' }}>실패</span>
                          )}
                          {(!order.refund_status || order.refund_status === 'none') && (
                            <span style={{ fontSize: 11, color: '#9CA3AF' }}>—</span>
                          )}

                          {order.status === 'paid' && order.refund_status !== 'completed' && order.refund_status !== 'processing' && (
                            <button
                              onClick={() => openRefundModal(order)}
                              style={{
                                marginTop: 2,
                                padding: '4px 10px',
                                borderRadius: 6,
                                border: '1px solid #FCA5A5',
                                background: '#FFFFFF',
                                color: '#DC2626',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              환불 처리
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ ...td, fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {formatDate(order.paid_at || order.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p style={{ marginTop: 16, fontSize: 12, color: '#6B7280', lineHeight: 1.7 }}>
          ※ 환불 컬럼의 <strong style={{ color: '#DC2626' }}>'환불 처리'</strong> 버튼을 클릭하면
          토스 결제 취소 + 쿠폰 회수 + 사용자 PRO 복원 + 결제자/사용자 안내 메일·SMS가 한 번에 자동 처리됩니다.
          (정책: 결제 후 7일 이내. 7일 초과 시 모달에서 강제 진행 옵션 활성화 가능)
        </p>
      </div>

      {/* ── 환불 처리 모달 ─────────────────────────────────────────────── */}
      {refundModalOrder && (
        <RefundModal
          order={refundModalOrder}
          preview={refundPreview}
          previewLoading={refundPreviewLoading}
          previewError={refundPreviewError}
          refundReason={refundReason}
          setRefundReason={setRefundReason}
          sendNotifications={refundSendNotifications}
          setSendNotifications={setRefundSendNotifications}
          forceOutsideWindow={refundForceOutsideWindow}
          setForceOutsideWindow={setRefundForceOutsideWindow}
          processing={refundProcessing}
          processError={refundProcessError}
          processResult={refundProcessResult}
          onClose={closeRefundModal}
          onSubmit={submitRefundProcess}
        />
      )}
    </div>
  );
};

const th = { padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '12px 14px', verticalAlign: 'top' };

const SummaryCard = ({ label, value, color }) => (
  <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px' }}>
    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// 환불 처리 모달
// ─────────────────────────────────────────────────────────────────────

const PREDICTED_ACTION_LABEL = {
  subscription_reverted_to_free: { text: 'PRO → free 복원', bg: '#FEF2F2', color: '#DC2626' },
  subscription_untouched_paid_pro_protected: { text: '실결제 PRO 보호 (미변경)', bg: '#EFF6FF', color: '#1E40AF' },
  subscription_untouched_already_free_or_expired: { text: '이미 free·만료 (미변경)', bg: '#F3F4F6', color: '#6B7280' },
};

const RefundModal = ({
  order,
  preview,
  previewLoading,
  previewError,
  refundReason,
  setRefundReason,
  sendNotifications,
  setSendNotifications,
  forceOutsideWindow,
  setForceOutsideWindow,
  processing,
  processError,
  processResult,
  onClose,
  onSubmit,
}) => {
  const stop = (e) => e.stopPropagation();

  // 처리 결과가 있으면 결과 화면 모드로 표시
  const isResultMode = !!processResult;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16, overflow: 'auto',
      }}
    >
      <div
        onClick={stop}
        style={{
          background: '#FFFFFF', borderRadius: 16, maxWidth: 720, width: '100%',
          maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
        }}
      >
        {/* 헤더 */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>
              {isResultMode ? '환불 처리 완료' : '환불 처리'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>
              {order.school_name} · {order.contact_name} · {order.email}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            style={{
              background: 'transparent', border: 'none', fontSize: 22, cursor: processing ? 'not-allowed' : 'pointer',
              color: '#6B7280', padding: '4px 8px', opacity: processing ? 0.4 : 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* 처리 결과 화면 */}
          {isResultMode && (
            <div>
              <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 14, color: '#065F46', fontWeight: 700 }}>✅ 환불 처리가 정상 완료되었습니다.</p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#047857', lineHeight: 1.7 }}>
                  환불 금액: <strong>₩{(processResult.refund_amount || 0).toLocaleString()}</strong> ·
                  토스: {processResult.toss_action} · 영향 사용자 {processResult.user_actions?.length || 0}명
                </p>
                {processResult.refund_receipt_url && (
                  <a
                    href={processResult.refund_receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block', marginTop: 12, fontSize: 13, color: '#1E40AF', fontWeight: 600 }}
                  >
                    토스 환불 매출전표 보기 →
                  </a>
                )}
              </div>

              {/* 사용자 처리 결과 */}
              {(processResult.user_actions || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>사용자 처리 결과</p>
                  <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, fontSize: 12 }}>
                    {processResult.user_actions.map((ua, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: idx < processResult.user_actions.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                        <span style={{ color: '#1F2937' }}>{ua.user_email}</span>
                        <span style={{ color: ua.error ? '#DC2626' : '#6B7280', fontFamily: 'ui-monospace,monospace' }}>
                          {ua.action}{ua.error ? ` (오류: ${ua.error})` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 알림 발송 결과 */}
              {processResult.notifications && !processResult.notifications.skipped && (
                <div style={{ marginBottom: 16, fontSize: 12, color: '#6B7280' }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 4px' }}>알림 발송 결과</p>
                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    <li>결제자 이메일: {String(processResult.notifications.payer_email_sent)}</li>
                    <li>결제자 알림톡/SMS: {String(processResult.notifications.payer_alimtalk_sent)}</li>
                    <li>사용자 이메일: {Array.isArray(processResult.notifications.user_emails_sent) ? `${processResult.notifications.user_emails_sent.length}건 발송` : String(processResult.notifications.user_emails_sent)}</li>
                  </ul>
                </div>
              )}

              <button
                onClick={onClose}
                style={{
                  width: '100%', padding: '12px 24px', background: '#3182F6', color: '#FFFFFF',
                  border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}
              >
                닫기
              </button>
            </div>
          )}

          {/* 미리보기/입력 화면 */}
          {!isResultMode && (
            <div>
              {previewLoading && (
                <p style={{ padding: 32, textAlign: 'center', color: '#6B7280' }}>환불 영향을 분석하는 중…</p>
              )}

              {previewError && (
                <div style={{ padding: 16, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, marginBottom: 16 }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#991B1B', fontWeight: 600 }}>미리보기 호출 실패</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#7F1D1D' }}>{previewError}</p>
                </div>
              )}

              {preview && (
                <>
                  {/* 카드 1: 결제 정보 */}
                  <div style={{ marginBottom: 16, padding: 14, border: '1px solid #E5E7EB', borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span style={{ color: '#6B7280' }}>주문번호</span>
                      <code style={{ fontSize: 11, color: '#1F2937' }}>{preview.order.order_id}</code>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span style={{ color: '#6B7280' }}>플랜·수량</span>
                      <span style={{ color: '#1F2937', fontWeight: 600 }}>{preview.order.plan_label} × {preview.order.quantity}개</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                      <span style={{ color: '#6B7280' }}>결제 금액</span>
                      <span style={{ color: '#3182F6', fontWeight: 700 }}>₩{(preview.refundable_amount || 0).toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#6B7280' }}>결제 후 경과</span>
                      <span style={{ color: preview.is_within_refund_window ? '#059669' : '#DC2626', fontWeight: 600 }}>
                        {preview.days_since_paid}일 ({preview.is_within_refund_window ? `${preview.refund_window_days}일 이내` : `${preview.refund_window_days}일 초과`})
                      </span>
                    </div>
                  </div>

                  {/* 카드 2: 토스 상태 */}
                  <div style={{ marginBottom: 16, padding: 14, border: '1px solid #E5E7EB', borderRadius: 10, background: '#F9FAFB' }}>
                    <p style={{ margin: 0, fontSize: 12, color: '#6B7280', fontWeight: 600 }}>토스 결제 상태</p>
                    <p style={{ margin: '6px 0 0', fontSize: 14, color: preview.toss_already_canceled ? '#92400E' : '#059669', fontWeight: 700 }}>
                      {preview.toss_payment_status || 'unknown'}
                      {preview.toss_already_canceled && ' (이미 취소됨 — 토스 환불은 스킵)'}
                    </p>
                  </div>

                  {/* 카드 3: 영향 분석 */}
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#374151' }}>
                      영향 분석 — 비활성화될 쿠폰 {preview.coupons_to_deactivate.length}개,
                      처리 대상 사용자 {preview.users_affected.length}명
                    </p>

                    {preview.users_affected.length === 0 && (
                      <p style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 13, background: '#F9FAFB', borderRadius: 8 }}>
                        쿠폰을 등록한 사용자가 없습니다. 마스터 쿠폰만 비활성화됩니다.
                      </p>
                    )}

                    {preview.users_affected.map((ua) => {
                      const label = PREDICTED_ACTION_LABEL[ua.predicted_action] || { text: ua.predicted_action, bg: '#F3F4F6', color: '#374151' };
                      return (
                        <div key={ua.user_coupon_id} style={{ padding: 12, border: '1px solid #E5E7EB', borderRadius: 8, marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 13, color: '#1F2937', fontWeight: 600 }}>{ua.user_email}</span>
                            <span style={{
                              display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11,
                              fontWeight: 700, background: label.bg, color: label.color,
                            }}>
                              {label.text}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: '#6B7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <span>쿠폰: <code style={{ fontFamily: 'ui-monospace,monospace' }}>{ua.coupon_code}</code></span>
                            <span>type: {ua.current_subscription_type || '-'}</span>
                            <span>method: {ua.current_payment_method || '-'}</span>
                            <span>end: {ua.current_end_date ? new Date(ua.current_end_date).toLocaleDateString('ko-KR') : '-'}</span>
                          </div>
                          {ua.other_active_coupons && ua.other_active_coupons.length > 0 && (
                            <div style={{ marginTop: 8, padding: 8, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, fontSize: 11, color: '#92400E' }}>
                              ⚠️ 다른 활성 쿠폰: {ua.other_active_coupons.map(c => c.code).join(', ')} —
                              free 복원 시 이 쿠폰들의 잔여 기간이 함께 사라질 수 있습니다.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* 경고 모음 */}
                  {(preview.warnings || []).length > 0 && (
                    <div style={{ marginBottom: 16, padding: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8 }}>
                      <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#92400E' }}>⚠️ 사전 경고</p>
                      <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 12, color: '#78350F', lineHeight: 1.7 }}>
                        {preview.warnings.map((w, idx) => <li key={idx}>{w}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* 환불 사유 입력 */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                      환불 사유 <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      maxLength={500}
                      placeholder="예: 왓퀴즈중학교 홍길동 선생님 요청"
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 8,
                        border: '1px solid #D1D5DB', fontSize: 13, color: '#1F2937',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* 옵션 */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={sendNotifications}
                      onChange={(e) => setSendNotifications(e.target.checked)}
                    />
                    결제자/사용자에게 자동 안내 발송 (이메일 + 알림톡/SMS)
                  </label>

                  {!preview.is_within_refund_window && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#991B1B', marginBottom: 10, cursor: 'pointer', padding: 10, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8 }}>
                      <input
                        type="checkbox"
                        checked={forceOutsideWindow}
                        onChange={(e) => setForceOutsideWindow(e.target.checked)}
                      />
                      <span><strong>강제 진행</strong>: 결제 후 {preview.days_since_paid}일 경과 — 7일 환불 정책 초과</span>
                    </label>
                  )}

                  {/* 처리 에러 */}
                  {processError && (
                    <div style={{ padding: 12, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, marginBottom: 12 }}>
                      <p style={{ margin: 0, fontSize: 12, color: '#991B1B', fontWeight: 600 }}>{processError}</p>
                    </div>
                  )}

                  {/* 마지막 경고 */}
                  <div style={{ padding: 12, background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, marginBottom: 16 }}>
                    <p style={{ margin: 0, fontSize: 12, color: '#7F1D1D', lineHeight: 1.7 }}>
                      ⚠️ 이 작업은 토스 환불 + 쿠폰 비활성화 + 사용자 PRO 회수를 즉시 실행합니다. <strong>되돌릴 수 없습니다.</strong>
                    </p>
                  </div>

                  {/* 버튼 */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={onClose}
                      disabled={processing}
                      style={{
                        flex: 1, padding: '12px 24px', background: '#FFFFFF', color: '#374151',
                        border: '1px solid #D1D5DB', borderRadius: 10, fontSize: 14, fontWeight: 600,
                        cursor: processing ? 'not-allowed' : 'pointer', opacity: processing ? 0.5 : 1,
                      }}
                    >
                      취소
                    </button>
                    <button
                      onClick={onSubmit}
                      disabled={processing || (!preview.is_within_refund_window && !forceOutsideWindow) || !refundReason.trim()}
                      style={{
                        flex: 2, padding: '12px 24px', background: processing ? '#FCA5A5' : '#DC2626',
                        color: '#FFFFFF', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                        cursor: processing ? 'wait' : 'pointer',
                      }}
                    >
                      {processing ? '처리 중…' : '환불 처리 실행'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPurchaseAdminPage;
